import { AssetType, DisplayAssetItem, JourneyAssetName } from 'lib/assetLoader'
import { cdnImageUrl, cdnVideoUrl } from 'lib/cdn'
import {
  JourneyAssetItem,
  JOURNEY_ASSET_DIMENSIONS,
} from 'lib/journeyAssetDimensions'
import themes, { ThemeName } from 'styles/theme'

export type VariantAssetManifest = { [k in ThemeName]: VariantAssets }

type JourneyAsset = Record<JourneyAssetName, DisplayAssetItem>

type VariantAssets = Record<
  | 'productOpen'
  | 'productPour'
  | 'water'
  | 'productAngle'
  | 'productCart'
  | 'productClosed'
  | 'productSide'
  | 'productTop'
  | 'productBottom'
  | JourneyAssetName,
  DisplayAssetItem
>

const scaleAsset = (width: number, height: number, longEdge: number) => {
  if (width > height) {
    const scaledWidth = Math.min(width, longEdge)
    const aspectRatio = height / width
    return {
      width: scaledWidth,
      height: Math.round(aspectRatio * scaledWidth),
    }
  }

  const scaledHeight = Math.min(height, longEdge)
  const aspectRatio = width / height
  return {
    width: Math.round(aspectRatio * scaledHeight),
    height: scaledHeight,
  }
}

const cdnAssetUrl = (
  width: number,
  height: number,
  type: AssetType,
  theme: ThemeName,
  assetName: JourneyAssetName
): string => {
  switch (type) {
    case 'video':
      return cdnVideoUrl(assetName, { width, theme })
    case 'image':
      return cdnImageUrl({ theme, imageName: assetName, width, height })
    default:
      throw new Error(`Cannot get asset URL, unknown type ${type}`)
  }
}

const JOURNEY_ASSET_BREAKPOINTS = {
  s: {
    longEdge: 375,
    maxScreenWidth: 376,
  },
  m: {
    longEdge: 800,
    maxScreenWidth: 1023,
  },
  l: {
    longEdge: 600,
    maxScreenWidth: 1919,
  },
  xl: {
    longEdge: 800,
    maxScreenWidth: Number.POSITIVE_INFINITY,
  },
}

const journeyAssetItem = (
  theme: ThemeName,
  assetName: JourneyAssetName,
  assetItem: JourneyAssetItem
): DisplayAssetItem => {
  const { type, width, height } = assetItem

  let srcSet
  let xlAsset

  if (assetName === 'Pattern-v5') {
    xlAsset = scaleAsset(width, height, 150)

    const sLongEdge = 140
    const sAsset = scaleAsset(width, height, sLongEdge)
    srcSet = [
      {
        src: {
          1: cdnAssetUrl(sAsset.width, sAsset.height, type, theme, assetName),
        },
        maxScreenWidth: JOURNEY_ASSET_BREAKPOINTS.m.maxScreenWidth,
        longEdge: sLongEdge,
      },
    ]
  } else if (assetName === 'Icon-v5') {
    xlAsset = scaleAsset(width, height, 225)

    const sLongEdge = 136
    const sAsset = scaleAsset(width, height, sLongEdge)
    srcSet = [
      {
        src: {
          1: cdnAssetUrl(sAsset.width, sAsset.height, type, theme, assetName),
        },
        maxScreenWidth: JOURNEY_ASSET_BREAKPOINTS.m.maxScreenWidth,
        longEdge: sLongEdge,
      },
    ]
  } else {
    const sAsset = scaleAsset(
      width,
      height,
      JOURNEY_ASSET_BREAKPOINTS.s.longEdge
    )
    const mAsset = scaleAsset(
      width,
      height,
      JOURNEY_ASSET_BREAKPOINTS.m.longEdge
    )
    const lAsset = scaleAsset(
      width,
      height,
      JOURNEY_ASSET_BREAKPOINTS.l.longEdge
    )
    xlAsset = scaleAsset(width, height, JOURNEY_ASSET_BREAKPOINTS.xl.longEdge)

    srcSet = [
      {
        src: {
          1: cdnAssetUrl(sAsset.width, sAsset.height, type, theme, assetName),
        },
        maxScreenWidth: JOURNEY_ASSET_BREAKPOINTS.s.maxScreenWidth,
        longEdge: JOURNEY_ASSET_BREAKPOINTS.s.longEdge,
      },
      {
        src: {
          1: cdnAssetUrl(mAsset.width, mAsset.height, type, theme, assetName),
        },
        maxScreenWidth: JOURNEY_ASSET_BREAKPOINTS.m.maxScreenWidth,
        longEdge: JOURNEY_ASSET_BREAKPOINTS.m.longEdge,
      },
      {
        src: {
          1: cdnAssetUrl(lAsset.width, lAsset.height, type, theme, assetName),
        },
        maxScreenWidth: JOURNEY_ASSET_BREAKPOINTS.l.maxScreenWidth,
        longEdge: JOURNEY_ASSET_BREAKPOINTS.l.longEdge,
      },
    ]
  }

  return {
    src: cdnAssetUrl(xlAsset.width, xlAsset.height, type, theme, assetName),
    srcSet,
    width: xlAsset.width,
    height: xlAsset.height,
    alt: '',
    type,
  }
}

export const journeyAssets = (theme: ThemeName): JourneyAsset => {
  const buildItem = (
    assetName: JourneyAssetName,
    assetItem: JourneyAssetItem
  ) => journeyAssetItem(theme, assetName, assetItem)

  const themeAssets = JOURNEY_ASSET_DIMENSIONS[theme]

  return {
    'Animation-v6': buildItem('Animation-v6', themeAssets['Animation-v6']),
    'For-the-Control-v6': buildItem(
      'For-the-Control-v6',
      themeAssets['For-the-Control-v6']
    ),
    'For-the-Eye-v5': buildItem(
      'For-the-Eye-v5',
      themeAssets['For-the-Eye-v5']
    ),
    'For-the-Feeling-v6': buildItem(
      'For-the-Feeling-v6',
      themeAssets['For-the-Feeling-v6']
    ),
    'For-the-Flow-v7': buildItem(
      'For-the-Flow-v7',
      themeAssets['For-the-Flow-v7']
    ),
    'For-the-Pure-v6': buildItem(
      'For-the-Pure-v6',
      themeAssets['For-the-Pure-v6']
    ),
    'For-the-Ride-v5': buildItem(
      'For-the-Ride-v5',
      themeAssets['For-the-Ride-v5']
    ),
    'For-the-Taste-v6': buildItem(
      'For-the-Taste-v6',
      themeAssets['For-the-Taste-v6']
    ),
    'Icon-v5': buildItem('Icon-v5', themeAssets['Icon-v5']),
    'Pattern-v5': buildItem('Pattern-v5', themeAssets['Pattern-v5']),
  }
}

const variantThemes = themes.filter((t) => t.name !== 'default')

type VariantAssetFn = (theme: ThemeName) => VariantAssets

export const generateManifest = (
  variantAssetFn: VariantAssetFn
): VariantAssetManifest =>
  variantThemes.reduce<VariantAssetManifest>(
    (acc, t) => ({
      ...acc,
      [t.name]: variantAssetFn(t.name),
    }),
    {} as any
  )
